
import React, { useState, useEffect } from 'react';
import { ActionName, ACTION_NAMES, CustomAction } from '../config';
import { useModalEffects } from '../hooks/useModalEffects';

interface SortOrderModalProps {
    isOpen: boolean;
    currentOrder: ActionName[];
    customActions: CustomAction[];
    onClose: () => void;
    onSave: (newOrder: ActionName[], newCustomActions: CustomAction[]) => void;
}

const SortOrderModal: React.FC<SortOrderModalProps> = ({ isOpen, currentOrder, customActions, onClose, onSave }) => {
    const [localOrder, setLocalOrder] = useState<ActionName[]>(currentOrder);
    const [localCustomActions, setLocalCustomActions] = useState<CustomAction[]>(customActions);
    
    // New Action Form State
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const [newPoints, setNewPoints] = useState<number>(20);

    useModalEffects(isOpen, onClose, 'sort-modal-content');

    useEffect(() => {
        if (isOpen) {
            setLocalOrder(currentOrder);
            setLocalCustomActions(customActions);
            setIsAdding(false);
            setNewName('');
            setNewPoints(20);
        }
    }, [isOpen, currentOrder, customActions]);

    const moveItem = (index: number, direction: 'up' | 'down') => {
        const newOrder = [...localOrder];
        if (direction === 'up' && index > 0) {
            [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
        } else if (direction === 'down' && index < newOrder.length - 1) {
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        }
        setLocalOrder(newOrder);
    };

    const handleAddCustomAction = () => {
        if (!newName.trim()) return;
        const id = `custom_${Date.now()}`;
        const newAction: CustomAction = {
            id,
            name: newName.trim(),
            points: newPoints
        };
        setLocalCustomActions([...localCustomActions, newAction]);
        setLocalOrder([...localOrder, id]);
        setNewName('');
        setNewPoints(20);
        setIsAdding(false);
    };

    const handleRemoveAction = (idToRemove: string) => {
        setLocalOrder(localOrder.filter(id => id !== idToRemove));
        setLocalCustomActions(localCustomActions.filter(c => c.id !== idToRemove));
    };

    const handleReset = () => {
        setLocalOrder([...ACTION_NAMES]);
        setLocalCustomActions([]);
    };

    const getDisplayName = (id: string) => {
        const custom = localCustomActions.find(c => c.id === id);
        return custom ? custom.name : id;
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-80 flex justify-center items-center z-50 p-4 transition-opacity duration-300"
            onClick={onClose}
        >
            <div 
                id="sort-modal-content"
                className="card bg-white dark:bg-slate-800 p-6 rounded-xl w-full max-w-md max-h-[90vh] flex flex-col transform transition-all duration-300 ease-out opacity-0 scale-95"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">設定計分項目與排序</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 bg-indigo-50 dark:bg-slate-700/30 p-3 rounded-lg flex justify-between items-center">
                    <div>
                        <p>調整按鈕順序或新增自訂計分項目。</p>
                        <p className="text-xs mt-1 opacity-80">排列順序：由左至右，由上至下。</p>
                    </div>
                    <button 
                        onClick={() => setIsAdding(!isAdding)}
                        className="p-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-800/50 transition-colors flex items-center justify-center"
                        title="新增自訂項目"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>

                {isAdding && (
                    <div className="mb-4 p-3 border border-indigo-200 dark:border-indigo-800 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/20">
                        <h3 className="text-sm font-semibold text-indigo-800 dark:text-indigo-300 mb-2">新增自訂項目</h3>
                        <div className="flex gap-2 mb-2">
                            <input 
                                type="text"
                                placeholder="項目名稱"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="flex-grow px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500"
                            />
                            <div className="flex items-center gap-1 w-24">
                                <input 
                                    type="number"
                                    placeholder="分數"
                                    value={newPoints}
                                    onChange={(e) => setNewPoints(Number(e.target.value))}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-500 dark:text-gray-400">pt</span>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => setIsAdding(false)}
                                className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 rounded"
                            >
                                取消
                            </button>
                            <button 
                                onClick={handleAddCustomAction}
                                disabled={!newName.trim()}
                                className="px-3 py-1 text-xs text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 rounded"
                            >
                                確認新增
                            </button>
                        </div>
                    </div>
                )}

                <div className="overflow-y-auto flex-grow mb-4 pr-1">
                    <ul className="grid grid-cols-2 gap-3">
                        {localOrder.map((action, index) => {
                            const isCustom = localCustomActions.some(c => c.id === action);
                            return (
                                <li key={action} className="flex flex-col justify-between bg-gray-50 dark:bg-slate-700/40 p-3 rounded-lg border border-gray-200 dark:border-slate-600 min-h-[110px] shadow-sm transition-colors duration-200 relative group">
                                    <div className="flex items-start justify-between w-full mb-2">
                                        <span className="text-xs font-mono text-gray-400 dark:text-gray-500 select-none">{(index + 1).toString().padStart(2, '0')}</span>
                                        {isCustom && (
                                            <button 
                                                onClick={() => handleRemoveAction(action)}
                                                className="text-gray-400 hover:text-red-500 transition-colors w-5 h-5 flex justify-center items-center rounded-full hover:bg-red-50 dark:hover:bg-red-900/30"
                                                title="刪除"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <span className="text-gray-800 dark:text-gray-200 font-bold text-center mb-3 leading-tight flex-grow flex items-center justify-center">
                                        {getDisplayName(action)}
                                    </span>
                                    <div className="flex items-center justify-between gap-2 bg-white dark:bg-slate-800/50 rounded-md p-1 border border-gray-100 dark:border-slate-600">
                                        <button 
                                            onClick={() => moveItem(index, 'up')}
                                            disabled={index === 0}
                                            className="flex-1 flex justify-center items-center py-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-600/50 disabled:opacity-20 disabled:cursor-not-allowed text-indigo-600 dark:text-indigo-400 transition-colors"
                                            aria-label="向前移動"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        <div className="w-px h-4 bg-gray-200 dark:bg-slate-600"></div>
                                        <button 
                                            onClick={() => moveItem(index, 'down')}
                                            disabled={index === localOrder.length - 1}
                                            className="flex-1 flex justify-center items-center py-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-600/50 disabled:opacity-20 disabled:cursor-not-allowed text-indigo-600 dark:text-indigo-400 transition-colors"
                                            aria-label="向後移動"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 15.707a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L8.586 10 4.293 14.293a1 1 0 000 1.414zm6 0a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L14.586 10l-4.293 4.293a1 1 0 000 1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 z-10">
                    <button
                        onClick={handleReset}
                        className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline"
                    >
                        恢復預設
                    </button>
                    <div className="flex space-x-3">
                        <button 
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600/50 transition-colors active:scale-95"
                        >
                            取消
                        </button>
                        <button 
                            onClick={() => onSave(localOrder, localCustomActions)}
                            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 dark:bg-indigo-700/80 rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors shadow-sm active:scale-95"
                        >
                            儲存變更
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SortOrderModal;
